import {AutoLinkNode, LinkNode} from '@lexical/link';
import {$convertToMarkdownString, $convertFromMarkdownString} from '@lexical/markdown';
import {ClickableLinkPlugin} from '@lexical/react/LexicalClickableLinkPlugin';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {captureException} from '@sentry/browser';
import {EditorState, LexicalEditor} from 'lexical';
import {Box, Show} from 'platform/foundation';

import {ReactElement, useEffect, useRef} from 'react';
import {useController} from 'react-hook-form';

import {MARKDOWN_TRANSFORMERS} from './config';
import {AutoFocusPlugin} from './plugins/AutoFocusPlugin';
import {AutoLinkPlugin} from './plugins/AutoLinkPlugin';
import {EditorRefPlugin} from './plugins/EditorRefPlugin';
import {LinkPlugin} from './plugins/LinkPlugin';
import {RawPastePlugin} from './plugins/RawPastePlugin';

export interface RichContentInputProps {
  name: string;
  autoFocus?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  ContentEditableComponent: ReactElement;
  PlaceholderComponent?: ReactElement;
}

export function RichContentInput(props: RichContentInputProps) {
  const {field} = useController({name: props.name});

  const lexicalEditorRef = useRef<LexicalEditor>(null);

  const onChange = (editorState: EditorState) => {
    editorState.read(() => {
      const markdown = $convertToMarkdownString(MARKDOWN_TRANSFORMERS);
      field.onChange(markdown);
    });
  };

  useEffect(() => {
    if (lexicalEditorRef.current) {
      lexicalEditorRef.current.update(() => {
        const currentMarkdown = $convertToMarkdownString(MARKDOWN_TRANSFORMERS);

        if (currentMarkdown !== field.value) {
          $convertFromMarkdownString(field.value, MARKDOWN_TRANSFORMERS);
        }
      });
    }
  }, [field.value]);

  return (
    <>
      <Box position="relative" width="100%">
        <LexicalComposer
          initialConfig={{
            namespace: 'chat-editor',
            editable: !field.disabled || !props.isDisabled,
            editorState: () => $convertFromMarkdownString(field.value, MARKDOWN_TRANSFORMERS),
            onError: (error) => {
              captureException(error);
            },
            nodes: [LinkNode, AutoLinkNode],
          }}
        >
          <RichTextPlugin
            contentEditable={props.ContentEditableComponent}
            placeholder={props.placeholder ? props.PlaceholderComponent : null}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <EditorRefPlugin editorRef={lexicalEditorRef} />
          <AutoLinkPlugin />
          <ClickableLinkPlugin />
          <LinkPlugin />
          <RawPastePlugin />
          <OnChangePlugin onChange={onChange} />
          <Show when={props.autoFocus}>
            <AutoFocusPlugin />
          </Show>
        </LexicalComposer>
      </Box>
    </>
  );
}
